if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"], [id*="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"], [id*="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"], [id*="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        
        // Natulim SP - Attach click listener to media-gallery itself (not thumbnails child)
        // This survives DOM replacements during variant changes in modal
        this.addEventListener('click', this.onThumbnailClick.bind(this));
        
        if (!this.elements.thumbnails) return;
        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      // Natulim SP - Helper to get fresh viewer element reference
      getViewer() {
        return this.querySelector('[id^="GalleryViewer"], [id*="GalleryViewer"]') || this.elements.viewer;
      }

      // Natulim SP - Helper to get fresh thumbnails element reference
      getThumbnails() {
        return this.querySelector('[id^="GalleryThumbnails"], [id*="GalleryThumbnails"]') || this.elements.thumbnails;
      }

      // Natulim SP - Event delegation handler for thumbnail clicks
      // Attached to media-gallery to survive DOM replacements
      onThumbnailClick(event) {
        // Only handle clicks within the thumbnails area
        const thumbnails = this.getThumbnails();
        if (!thumbnails || !thumbnails.contains(event.target)) return;
        
        const button = event.target.closest('button');
        if (!button) return;
        
        const thumbnailItem = button.closest('[data-target]');
        if (!thumbnailItem) return;
        
        event.preventDefault();
        this.setActiveMedia(thumbnailItem.dataset.target, false);
      }

      onSlideChanged(event) {
        const thumbnails = this.getThumbnails();
        if (!thumbnails) return;
        const thumbnail = thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const viewer = this.getViewer();
        if (!viewer) return;
        
        // Natulim SP - Handle quickadd- prefix mismatch between thumbnails and gallery slides
        // Thumbnails may have quickadd- prefix in data-target, but slides may not have it in data-media-id
        let activeMedia = viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia && mediaId.includes('quickadd-')) {
          // Try without quickadd- prefix
          const mediaIdWithoutPrefix = mediaId.replace('quickadd-', '');
          activeMedia = viewer.querySelector(`[data-media-id="${mediaIdWithoutPrefix}"]`);
        }
        if (!activeMedia) {
          // Fallback to first media item
          activeMedia = viewer.querySelector('[data-media-id]');
        }
        if (!activeMedia) {
          return;
        }
        viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        const thumbnails = this.getThumbnails();

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (thumbnails) {
            // Natulim SP - Try both with and without quickadd- prefix
            let activeThumbnail = thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (!activeThumbnail) {
              const actualMediaId = activeMedia.dataset.mediaId;
              activeThumbnail = thumbnails.querySelector(`[data-target="${actualMediaId}"]`) ||
                               thumbnails.querySelector(`[data-target*="${actualMediaId}"]`);
            }
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (viewer.slider) viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          // Natulim (ago-2026) — los layouts 'carousel' y 'hide' son sliders
          // horizontales tambien en desktop (sin miniaturas que hagan de
          // indice), asi que ahi tambien hay que desplazarse a la media
          // activa al cambiar de variante.
          const isDesktopCarousel = (this.dataset.desktopLayout || '') === 'carousel';
          const isDesktopHide = (this.dataset.desktopLayout || '') === 'hide';
          if (!this.mql.matches || thumbnails || isDesktopCarousel || isDesktopHide) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!thumbnails) return;
        // Natulim SP - Try both with and without quickadd- prefix for thumbnail lookup
        let activeThumbnail = thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) {
          const actualMediaId = activeMedia.dataset.mediaId;
          activeThumbnail = thumbnails.querySelector(`[data-target="${actualMediaId}"]`) ||
                           thumbnails.querySelector(`[data-target*="${actualMediaId}"]`);
        }
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        const thumbnails = this.getThumbnails();
        if (!thumbnails || !thumbnail) return;

        thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', true);
        if (thumbnails.isSlideVisible && thumbnails.isSlideVisible(thumbnail, 10)) return;

        if (thumbnails.slider) {
          thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
